/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/BuildPolicies" />
import * as React from "react";
import { IBuildPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableArray, IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Filter, getKeywordFilterItem } from "VSSUI/Filter";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { ColumnMore, ColumnSorting, ITableColumn, ITableRow, SimpleTableCell, sortItems, SortOrder, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { Filter as FilterStore, FILTER_APPLIED_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import { IPoliciesViewService, policyTypes } from "../Services/PoliciesViewService";
import { enabledColumn, inheritanceColumn, IPolicyColumn, pathColumn } from "./Columns";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { PolicyCard } from "./PolicyCard";

interface IBuildPoliciesProps {
    isEditable: boolean;
    ready: boolean;
    policies: IReadonlyObservableArray<IBuildPolicy>;
}

const loadingItems = new ObservableArray<IBuildPolicy>(new Array(3).fill(new ObservableValue<IBuildPolicy | undefined>(undefined)));

export class BuildPolicies extends VssComponent<IBuildPoliciesProps> {
    private columns: IPolicyColumn<IBuildPolicy>[];
    private filter: IFilter;
    private filteredItems = new ObservableArray<IBuildPolicy | IReadonlyObservableValue<IBuildPolicy | undefined>>(
        new Array(5).fill(new ObservableValue<IBuildPolicy | undefined>(undefined))
    );
    private policyService: IPoliciesViewService;
    private sortColumnIndex: number = 1;
    private sortFunctions: ((a: IBuildPolicy, b: IBuildPolicy) => number)[];
    private sortOrder: SortOrder = SortOrder.ascending;
    private checkQueueBuildPermissionOnPolicy: boolean;

    private sortingBehavior = new ColumnSorting<IBuildPolicy>((columnIndex, order) => {
        this.sortColumnIndex = columnIndex;
        this.sortOrder = order;
        this.refreshItems();
    });

    public constructor(props: IBuildPoliciesProps, context: IVssComponentContext) {
        super(props, context);

        this.filter = new FilterStore();

        this.policyService = context.pageContext.getService<IPoliciesViewService>("PoliciesViewService");

        this.columns = this.getColumns();
        this.sortFunctions = this.columns.map(c => c.sortFunction!);
        this.refreshItems();

        this.observe([{ propertyName: "policies", observer: this.refreshItems }]);
        this.checkQueueBuildPermissionOnPolicy = isFeatureFlagEnabled(context.pageContext, "Build2.CheckQueueBuildPermissionOnPipeline", false);
    }

    public componentDidMount = () => {
        this.filter.subscribe(this.refreshItems, FILTER_APPLIED_EVENT);
    };

    public componentWillUnmount() {
        this.filter.unsubscribe(this.refreshItems);
    }

    public render = (): JSX.Element => {
        const { ready } = this.props;
        return (
            <Observer items={this.filteredItems}>
                {() => {
                    const itemCount = this.filteredItems.length;
                    return (
                        <PolicyCard
                            className="flex-grow policies"
                            filter={<Filter filterStore={this.filter} filterItems={[getKeywordFilterItem(this.filter, 400)]} items={[]} />}
                            headerCommandBarItems={this.getCommandBarItems()}
                            headerDescriptionText={Resources.BuildValidationDesc}
                            itemCount={itemCount}
                            policyName="build"
                            titleText={Resources.BuildValidation}
                        >
                            <div className="settings-policies-wrapper">
                                {!ready || itemCount ? (
                                    <Table<IBuildPolicy>
                                        behaviors={[this.sortingBehavior]}
                                        itemProvider={ready ? this.filteredItems : loadingItems}
                                        columns={this.columns}
                                        onActivate={(event, row) => {
                                            if (!event.defaultPrevented) {
                                                this.onEdit(row);
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="flex-grow secondary-text padding-horizontal-20">{Resources.ZeroBuild}</div>
                                )}
                            </div>
                        </PolicyCard>
                    );
                }}
            </Observer>
        );
    };

    private refreshItems = () => {
        const filterItemState = this.filter.getState()["keyword"];
        const filter = filterItemState && filterItemState.value ? filterItemState.value.toLocaleLowerCase() : "";

        let items = [...this.props.policies.value];
        if (filter) {
            items = items.filter(pol => {
                const { displayName, filenamePatterns, buildDefinition } = pol.complexSettings!;
                return (
                    (buildDefinition && buildDefinition.name && buildDefinition.name.toLocaleLowerCase().indexOf(filter) !== -1) ||
                    (displayName && displayName.toLowerCase().indexOf(filter) !== -1) ||
                    (filenamePatterns && filenamePatterns.toLowerCase().indexOf(filter) !== -1)
                );
            });
        }

        this.filteredItems.value = sortItems(this.sortColumnIndex, this.sortOrder, this.sortFunctions, this.columns, items);
    };

    private getColumns = (): IPolicyColumn<IBuildPolicy>[] => {
        return [
            enabledColumn,
            {
                columnLayout: TableColumnLayout.twoLine,
                id: "name",
                name: Resources.Name,
                readonly: true,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IBuildPolicy>,
                    tableItem: IBuildPolicy
                ): JSX.Element => {
                    const cellValue = getRowTitle(tableItem);

                    return (
                        <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                            <div className="flex-column text-ellipsis">
                                <div className="text-ellipsis">
                                    <Tooltip text={cellValue}>
                                        <span className="body-m font-weight-semibold">{cellValue}</span>
                                    </Tooltip>
                                </div>
                                <div className="text-ellipsis">
                                    <span className="secondary-text body-s">
                                        {tableItem.complexSettings!.required ? Resources.Required : Resources.Optional}
                                    </span>
                                </div>
                            </div>
                        </SimpleTableCell>
                    );
                },
                sortFunction: (a, b) => localeIgnoreCaseComparer(getRowTitle(a), getRowTitle(b)),
                sortProps: { ariaLabelAscending: Resources.SortByNameAsc, ariaLabelDescending: Resources.SortByNameDesc },
                width: -30
            },
            pathColumn,
            {
                columnLayout: TableColumnLayout.twoLine,
                id: "trigger",
                name: Resources.Trigger,
                readonly: true,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IBuildPolicy>,
                    tableItem: IBuildPolicy
                ): JSX.Element => {
                    const cellValue = tableItem.complexSettings!.manualQueueOnly ? Resources.TriggerManual : Resources.TriggerAutomatic;
                    const expiration = !tableItem.complexSettings!.queueOnSourceUpdateOnly
                        ? Resources.ExpiresOnChange
                        : tableItem.complexSettings!.validDuration === "0"
                        ? Resources.ExpiresNever
                        : tableItem.complexSettings!.validDuration === "1"
                        ? Resources.ExpiresSingle
                        : format(Resources.ExpiresPlural, tableItem.complexSettings!.validDuration);
                    return (
                        <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                            <div className="flex-column text-ellipsis">
                                <div className="text-ellipsis">
                                    <Tooltip text={cellValue}>
                                        <span className="body-m">{cellValue}</span>
                                    </Tooltip>
                                </div>
                                <div className="text-ellipsis">
                                    <span className="secondary-text body-s">{expiration}</span>
                                </div>
                            </div>
                        </SimpleTableCell>
                    );
                },
                sortFunction: (a, b) =>
                    // Sort by manual/automatic, then by expire on change, then by expiration time
                    booleanComparer(a.complexSettings!.manualQueueOnly, b.complexSettings!.manualQueueOnly) ||
                    booleanComparer(a.complexSettings!.queueOnSourceUpdateOnly, b.complexSettings!.queueOnSourceUpdateOnly) ||
                    (parseInt(a.complexSettings!.validDuration) || 0) - (parseInt(b.complexSettings!.validDuration) || 0),
                sortProps: { ariaLabelAscending: Resources.SortByTriggerAsc, ariaLabelDescending: Resources.SortByTriggerDesc },
                width: -30
            },
            inheritanceColumn,
            new ColumnMore(this.getMoreMenuItems, this.showMoreMenu)
        ];
    };

    private getCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                ariaLabel: Resources.AddNewBuildPolicy,
                iconProps: { iconName: "Add" },
                id: "add-build-policy",
                disabled: !this.props.isEditable,
                onActivate: () => {
                    const newPolicy = this.policyService.getNewPolicy<IBuildPolicy>(policyTypes.build);
                    this.policyService.showEditPanel(newPolicy);
                }
            }
        ];
    };

    private getMoreMenuItems = (item: IBuildPolicy): IMenuProps => {
        const itemArr: IObservableArray<IMenuItem> = new ObservableArray([
            {
                id: "edit-view",
                text: Resources.EditView,
                data: item,
                onActivate: this.onEdit,
                iconProps: {
                    iconName: "View"
                },
                disabled: this.checkQueueBuildPermissionOnPolicy && !item.complexSettings?.buildDefinition?.hasQueueBuildPermission,
                secondaryText: this.checkQueueBuildPermissionOnPolicy && !item.complexSettings?.buildDefinition?.hasQueueBuildPermission ? Resources.MissingQueueBuildPermissionOnEditTooltip : undefined
            }
        ]);

        if (!item.isInherited && !item.isEnterpriseManaged) {
            itemArr.push({
                id: "delete",
                text: Resources.Delete,
                data: item,
                onActivate: this.onDelete,
                iconProps: {
                    iconName: "Delete"
                }
            });
        }

        const menuProps: IMenuProps = {
            id: "sub-menu",
            items: itemArr
        };

        return menuProps;
    };

    private onDelete = (menuItem: IMenuItem) => {
        const policy: IBuildPolicy = menuItem.data;
        showMessageDialog(this.context.pageContext, {
            lightDismiss: true,
            message: this.checkQueueBuildPermissionOnPolicy && !policy.complexSettings?.buildDefinition?.hasQueueBuildPermission ? Resources.ConfirmDeletionMessageNoQueueBuildPermission : Resources.ConfirmDeletionMessage,
            okButtonProps: { danger: true, primary: false, text: Resources.Delete },
            title: Resources.ConfirmDeletion,
            onClose: result => {
                if (result) {
                    this.policyService.deleteBuildPolicy(policy);
                }
            }
        });
    };

    private onEdit = (item: IMenuItem | ITableRow<IBuildPolicy>) => {
        this.policyService.showEditPanel(item.data);
    };

    private showMoreMenu = (item: IBuildPolicy): boolean => {
        return !item.isInherited;
    };
}

function getRowTitle(policy: IBuildPolicy) {
    const settings = policy.complexSettings!;
    return settings.displayName || (settings.buildDefinition ? settings.buildDefinition.name : "");
}

function booleanComparer(a: boolean, b: boolean): number {
    return a === b ? 0 : a ? 1 : -1;
}
